import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import API from '../../utils/api';

export const fetchCart = createAsyncThunk('cart/fetchCart', async (_, { rejectWithValue }) => {
  try {
    const { data } = await API.get('/cart');
    return data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message);
  }
});

export const addToCart = createAsyncThunk('cart/addToCart', async (itemData, { rejectWithValue }) => {
  try {
    const { data } = await API.post('/cart', itemData);
    return data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to add to cart');
  }
});

// Optimistic update: the reducer applies the new quantity immediately and
// stashes a snapshot of the previous cart in `meta.arg.__snapshot` so we can
// roll back on rejection. The server response (with fresh availability flags)
// supersedes our optimistic copy when the request settles.
export const updateCartItem = createAsyncThunk(
  'cart/updateItem',
  async ({ itemId, quantity }, { rejectWithValue }) => {
    try {
      const { data } = await API.put(`/cart/${itemId}`, { quantity });
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message);
    }
  }
);

export const removeFromCart = createAsyncThunk(
  'cart/removeItem',
  async (itemId, { rejectWithValue }) => {
    try {
      const { data } = await API.delete(`/cart/${itemId}`);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message);
    }
  }
);

export const clearCart = createAsyncThunk('cart/clearCart', async (_, { rejectWithValue }) => {
  try {
    await API.delete('/cart');
    return { items: [] };
  } catch (error) {
    return rejectWithValue(error.response?.data?.message);
  }
});

export const applyCoupon = createAsyncThunk('cart/applyCoupon', async ({ code, orderAmount }, { rejectWithValue }) => {
  try {
    const { data } = await API.post('/coupons/validate', { code, orderAmount });
    return data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Invalid coupon');
  }
});

const cartSlice = createSlice({
  name: 'cart',
  initialState: {
    items: [],
    coupon: null,
    loading: false,
    error: null,
    // Server-derived: { ok: false } when at least one line has a stock issue.
    // Used by CartPage to disable checkout + show a banner.
    availability: { ok: true },
    // Stack of pre-mutation snapshots so concurrent optimistic edits can
    // each unwind independently if their request fails.
    _snapshots: {},
  },
  reducers: {
    removeCoupon: (state) => { state.coupon = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCart.fulfilled, (state, { payload }) => {
        state.items = payload.cart.items || [];
        state.availability = payload.cart.availability || { ok: true };
      })
      // Add
      .addCase(addToCart.pending, (state) => { state.loading = true; })
      .addCase(addToCart.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.items = payload.cart.items || [];
        state.availability = payload.cart.availability || { ok: true };
      })
      .addCase(addToCart.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      })
      // Update — optimistic on pending
      .addCase(updateCartItem.pending, (state, { meta }) => {
        const { itemId, quantity } = meta.arg;
        state._snapshots[meta.requestId] = JSON.parse(JSON.stringify(state.items));
        const item = state.items.find((it) => it._id === itemId);
        if (item) item.quantity = quantity;
      })
      .addCase(updateCartItem.fulfilled, (state, { payload, meta }) => {
        delete state._snapshots[meta.requestId];
        state.items = payload.cart.items || [];
        state.availability = payload.cart.availability || { ok: true };
      })
      .addCase(updateCartItem.rejected, (state, { meta }) => {
        // Roll back — restore the snapshot we took on `pending`.
        const snap = state._snapshots[meta.requestId];
        if (snap) state.items = snap;
        delete state._snapshots[meta.requestId];
      })
      // Remove — optimistic
      .addCase(removeFromCart.pending, (state, { meta }) => {
        state._snapshots[meta.requestId] = JSON.parse(JSON.stringify(state.items));
        state.items = state.items.filter((it) => it._id !== meta.arg);
      })
      .addCase(removeFromCart.fulfilled, (state, { payload, meta }) => {
        delete state._snapshots[meta.requestId];
        state.items = payload.cart.items || [];
        state.availability = payload.cart.availability || { ok: true };
      })
      .addCase(removeFromCart.rejected, (state, { meta }) => {
        const snap = state._snapshots[meta.requestId];
        if (snap) state.items = snap;
        delete state._snapshots[meta.requestId];
      })
      .addCase(clearCart.fulfilled, (state) => {
        state.items = [];
        state.coupon = null;
        state.availability = { ok: true };
      })
      .addCase(applyCoupon.fulfilled, (state, { payload }) => {
        state.coupon = payload.coupon;
      })
      .addCase(applyCoupon.rejected, (state, { payload }) => {
        state.error = payload;
      });
  },
});

export const { removeCoupon } = cartSlice.actions;
export default cartSlice.reducer;
